/**
 * Processor type classification
 */
export type ProcessorType = 'SCRIPTED' | 'AGENTIC' | 'UTILITY' | 'ORCHESTRATOR' | 'VECTOR';

/**
 * Processor metadata from backend API
 * Matches backend/models/processor.py ProcessorMetadata
 */
export interface ProcessorMetadata {
  processor_name: string;
  display_name?: string;
  processor_type: ProcessorType;
  description: string;
  required_input_keys: string[];
  optional_input_keys: string[];
  output_keys: string[];
  output_types?: Record<string, string>; // Mapping of output_key → artifact_type
  default_config: Record<string, any> & {
    supports_dynamic_inputs?: boolean;
  };
}

/**
 * Grouped processors for palette display
 */
export interface ProcessorGroup {
  type: ProcessorType;
  label: string;
  processors: ProcessorMetadata[];
}

/**
 * Color mapping for processor types
 */
export const PROCESSOR_COLORS: Record<ProcessorType, {
  border: string;
  bg: string;
  text: string;
  badge: string;
}> = {
  SCRIPTED: {
    border: 'border-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
  },
  AGENTIC: {
    border: 'border-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
  },
  UTILITY: {
    border: 'border-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-300',
    badge: 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200',
  },
  ORCHESTRATOR: {
    border: 'border-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-300',
    badge: 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200',
  },
  VECTOR: {
    border: 'border-teal-500',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    text: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
  },
};

/**
 * Label mapping for processor types
 */
export const PROCESSOR_TYPE_LABELS: Record<ProcessorType, string> = {
  SCRIPTED: 'Scripted',
  AGENTIC: 'Agentic',
  UTILITY: 'Utility',
  ORCHESTRATOR: 'Orchestrator',
  VECTOR: 'Vector',
};

